import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button as MuiButton,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AddCircleOutlineOutlined, ModeOutlined, SaveOutlined, TrackChanges } from "@mui/icons-material";
import CustomTable from "../../common/CustomTable";
import CustomIconButton from "../../common/CustomIconButton";
import { useModalStyles } from "../../common/useModalStyles";
import SalesConfigTargetRateEditorPanel, {
  SalesConfigTargetRateEditorPanelHandle,
} from "./SalesConfigTargetRateEditorPanel";
import {
  SalesConfigRecord,
  SalesConfigTargetRateFormRow,
  SalesConfigTargetRatesFormValues,
  validateSalesConfigTargetRangeOverlap,
} from "./isrTypes";

interface SalesConfigTargetRatesPopupProps {
  open: boolean;
  salesConfig: SalesConfigRecord | null;
  onClose: () => void;
  onSubmitForm: (data: SalesConfigTargetRatesFormValues) => void;
  onValidationError?: (message: string) => void;
}

type EditorMode = null | "add" | "edit";

const mapConfigToRates = (salesConfig?: SalesConfigRecord | null): SalesConfigTargetRateFormRow[] =>
  salesConfig?.targetRates
    ?.filter((rate) => String(rate.targetRange ?? "").trim() !== "" || String(rate.targetCompPercent ?? "").trim() !== "")
    .map((rate) => ({
      targetRange: rate.targetRange ?? "",
      targetCompPercent: rate.targetCompPercent ?? "",
      active: rate.active !== false,
    })) || [];

const SalesConfigTargetRatesPopup: React.FC<SalesConfigTargetRatesPopupProps> = ({
  open,
  salesConfig,
  onClose,
  onSubmitForm,
  onValidationError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const [rates, setRates] = useState<SalesConfigTargetRateFormRow[]>([]);
  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const editorRef = useRef<SalesConfigTargetRateEditorPanelHandle>(null);

  useEffect(() => {
    if (!open) {
      setEditorMode(null);
      setEditIndex(null);
      setRates([]);
      return;
    }
    setRates(mapConfigToRates(salesConfig));
    setEditorMode(null);
    setEditIndex(null);
  }, [open, salesConfig]);

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const tableSx = {
    border: `1px solid ${tokens.border}`,
    borderRadius: "10px",
    "& .MuiTableCell-root": { borderColor: listBorderColor },
  };

  const tableRows = useMemo(
    () => rates.map((row, index) => ({
      uniqueId: index,
      targetRange: row.targetRange,
      compDisplay: row.targetCompPercent ? `${row.targetCompPercent}%` : "—",
      activeDisplay: row.active !== false ? "Active" : "Inactive",
    })),
    [rates]
  );

  const closeEditor = () => {
    setEditorMode(null);
    setEditIndex(null);
  };

  const handleAdd = () => {
    setEditIndex(null);
    setEditorMode("add");
  };

  const handleEdit = (index: number) => {
    setEditIndex(index);
    setEditorMode("edit");
  };

  const mergeEditorRowIntoRates = (
    currentRates: SalesConfigTargetRateFormRow[],
    row: SalesConfigTargetRateFormRow,
    mode: Exclude<EditorMode, null>,
    index: number | null
  ): SalesConfigTargetRateFormRow[] | null => {
    const rangeKey = row.targetRange.trim().toLowerCase();
    const isDuplicate = currentRates.some(
      (item, idx) => (mode !== "edit" || idx !== index)
        && item.targetRange.trim().toLowerCase() === rangeKey
    );
    if (isDuplicate) {
      onValidationError?.("Duplicate target range is not allowed.");
      return null;
    }
    const existingLabels = currentRates.map((item) => item.targetRange);
    const overlapValidation = validateSalesConfigTargetRangeOverlap(
      row.targetRange,
      existingLabels,
      mode === "edit" ? index : null
    );
    if (overlapValidation !== true) {
      onValidationError?.(overlapValidation);
      return null;
    }
    if (mode === "edit" && index != null) {
      return currentRates.map((item, idx) => (idx === index ? row : item));
    }
    return [...currentRates, row];
  };

  const handleSaveAll = async () => {
    let ratesToSave = rates;
    if (editorMode) {
      const pendingRow = await editorRef.current?.commit();
      if (!pendingRow) {
        return;
      }
      const merged = mergeEditorRowIntoRates(
        rates,
        pendingRow,
        editorMode,
        editIndex
      );
      if (!merged) {
        return;
      }
      ratesToSave = merged;
      setRates(merged);
      closeEditor();
    }
    onSubmitForm({ targetRates: ratesToSave });
  };

  if (!open) {
    return null;
  }

  const subtitle = salesConfig?.isrName
    ? `${salesConfig.isrName} · Invoice year ${salesConfig.invoiceYear || "—"}`
    : "Manage target ranges and comp % for this config.";

  const addButtonDisabled = editorMode != null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{ sx: { ...paperSx(isMobile), minHeight: isMobile ? undefined : 420 } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <TrackChanges sx={{ fontSize: 20, color: tokens.accent }} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              Target Based Rates
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={{ ...bodySx, display: "flex", flexDirection: "column", gap: 2 }}>
          <CustomTable
            columns={[
              { id: "targetRange", label: "Target Range" },
              { id: "compDisplay", label: "Comp %" },
              { id: "activeDisplay", label: "Status" },
              { id: "action", label: "Action" },
            ]}
            rows={tableRows}
            actionComponent={(row) => (
              <Box component="span" onClick={(event) => event.stopPropagation()} sx={{ display: "inline-flex" }}>
                <CustomIconButton
                  color="info"
                  size="small"
                  variant="outlined"
                  tooltipMessage="Edit"
                  tooltipPlacement="top"
                  onClick={() => handleEdit(row.uniqueId as number)}
                >
                  <ModeOutlined fontSize="small" />
                </CustomIconButton>
              </Box>
            )}
            onRowClick={(row) => handleEdit(row.uniqueId as number)}
            disablePagination
            emptyText="No target rate(s) added yet"
            uniqueValue="uniqueId"
            sx={tableSx}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <MuiButton
              type="button"
              variant="contained"
              disableElevation
              disabled={addButtonDisabled}
              startIcon={<AddCircleOutlineOutlined sx={{ fontSize: "1rem !important" }} />}
              onClick={handleAdd}
              sx={{
                ...primaryBtnSx,
                py: 0.8,
                px: 1.6,
                fontSize: "0.82rem",
                color: "#fff",
                "&.Mui-disabled": {
                  color: isDark ? "rgba(226,232,240,0.45)" : "rgba(255,255,255,0.55)",
                  background: isDark ? "rgba(100,116,139,0.35)" : "rgba(148,163,184,0.45)",
                  boxShadow: "none",
                  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                    color: isDark ? "rgba(226,232,240,0.4)" : "rgba(255,255,255,0.5)",
                  },
                },
              }}
            >
              Add Target Rate
            </MuiButton>
          </Box>

          {editorMode && (
            <SalesConfigTargetRateEditorPanel
              ref={editorRef}
              mode={editorMode}
              row={editorMode === "edit" && editIndex != null ? rates[editIndex] : null}
              existingRanges={rates.map((item) => item.targetRange)}
              excludeIndex={editorMode === "edit" ? editIndex : null}
              onCancel={closeEditor}
            />
          )}
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexDirection: { xs: "column", sm: "row" } }}>
            {editorMode && (
              <Box
                component="button"
                type="button"
                onClick={handleSaveAll}
                sx={{
                  ...primaryBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                <SaveOutlined sx={{ fontSize: "1.1rem" }} />
                Save Rates
              </Box>
            )}
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SalesConfigTargetRatesPopup;
